/**
 * The mandated import site for every routing primitive in `packages/app` — importing
 * `react-router-dom` directly is a Biome `noRestrictedImports` error. Most exports here are
 * pass-throughs; `useNavigate`, `Link`, and `LinkButton` are wrapped to inject the workspace slug.
 */
import {
  matchRoutes,
  NavigateFunction,
  NavigateOptions,
  To,
  useBeforeUnload,
  useBlocker,
  useParams,
  useLocation,
  useMatch as useRRMatch,
  useMatches as useRRMatches,
  useOutletContext,
  useSearchParams,
  Outlet,
  Route,
  Routes,
  createSearchParams,
  NavigateProps,
  Link as RRLink,
  useNavigate as RRuseNavigate,
  Navigate as RRNavigate,
  LinkProps as RRLinkProps,
  MemoryRouter,
  generatePath,
  useNavigation,
  RouteObject as RRRouteObject,
  LoaderFunction,
  PathPattern,
  useLoaderData,
  useRevalidator,
} from "react-router-dom";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext as useReactContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import {
  Link as HTLink,
  LinkButton as HTLinkButton,
  LinkButtonProps as HTLinkButtonProps,
  LinkProps as HTLinkProps,
} from "@hightouch/htui";

// Modify paths to include workspace slug when available
const modifyToWithWorkspace = (
  to: To | number,
  root_workspace_slug: string | undefined,
): To | number => {
  if (typeof to === "number") {
    return to;
  }
  if (!root_workspace_slug) {
    return to;
  }
  if (typeof to === "string") {
    if (to.startsWith("/")) {
      return `/${root_workspace_slug}${to}`;
    }
    return to;
  }
  if (to?.pathname?.startsWith("/")) {
    return { ...to, pathname: `/${root_workspace_slug}${to.pathname}` };
  }

  return to;
};

const useNavigate = () => {
  const navigate = RRuseNavigate();
  const { root_workspace_slug } = useParams<{ root_workspace_slug: string }>();

  const navigateWithWorkspace: NavigateFunction = useCallback(
    (to: To | number, options: NavigateOptions = {}) => {
      const processedTo = modifyToWithWorkspace(to, root_workspace_slug);
      if (typeof processedTo === "number") {
        return navigate(processedTo);
      }
      return navigate(processedTo, options);
    },
    [navigate, root_workspace_slug],
  );

  return navigateWithWorkspace;
};

const useMatch = (path: string | PathPattern<string>) => {
  const { root_workspace_slug } = useParams<{ root_workspace_slug: string }>();
  const match = useRRMatch(`/${root_workspace_slug}${path}`);

  return match;
};

const useMatches = useRRMatches;

const Navigate = ({ to, ...props }: NavigateProps) => {
  const { root_workspace_slug } = useParams<{ root_workspace_slug: string }>();
  return (
    <RRNavigate
      to={modifyToWithWorkspace(to, root_workspace_slug) as To}
      {...props}
    />
  );
};

const useOriginWithWorkspace = () => {
  const { root_workspace_slug } = useParams<{ root_workspace_slug: string }>();
  const origin = window.location.origin;

  if (!root_workspace_slug) {
    return origin;
  }

  return `${origin}/${root_workspace_slug}`;
};

const RouterLink = forwardRef<
  HTMLAnchorElement,
  RRLinkProps & { preload?: "mount" | "intent" }
>(({ to, preload, ...props }, ref) => {
  const { root_workspace_slug } = useParams<{
    root_workspace_slug: string;
  }>();

  const prefetch = useCallback(() => {
    const path = typeof to === "string" ? to : to.pathname;
    if (path) {
      // Preload failures are safe to ignore — the actual navigation
      // will retry the import via React.lazy with proper error handling.
      import("src/router/route-preloads")
        .then(({ preloadRoute }) => preloadRoute(path))
        .catch(() => {});
    }
  }, [to]);

  useEffect(() => {
    if (preload === "mount") prefetch();
  }, [preload, prefetch]);

  return (
    <RRLink
      ref={ref}
      to={modifyToWithWorkspace(to, root_workspace_slug) as To}
      {...(preload === "intent" && {
        onMouseEnter: prefetch,
        onFocus: prefetch,
      })}
      {...props}
    />
  );
});

RouterLink.displayName = "RouterLink";

type AnchorProps = {
  target?: string;
  rel?: string;
  download?: string;
};

const LinkButton = forwardRef<
  HTMLAnchorElement,
  HTLinkButtonProps & AnchorProps & { state?: NavigateOptions["state"] }
>(({ href, state, ...props }, ref) => {
  // If the button is disabled, we don't want to use the RouterLink,
  // because that will still allow a navigation event to be triggered.
  // So instead fallback to a normal HTUI LinkButton when disabled.
  if (props.isDisabled) {
    return <HTLinkButton href={href} {...props} ref={ref} />;
  }

  return (
    <HTLinkButton
      as={RouterLink}
      // @ts-expect-error RouterLink prop forwarded through polymorphic component
      to={href}
      state={state}
      {...props}
      ref={ref}
    />
  );
});

LinkButton.displayName = "LinkButton";

const Link = forwardRef<
  HTMLAnchorElement,
  HTLinkProps & AnchorProps & { state?: NavigateOptions["state"] }
>(({ href, state, ...props }, ref) => {
  return (
    <HTLink
      as={RouterLink}
      // @ts-expect-error RouterLink prop forwarded through polymorphic component
      to={href}
      state={state}
      ref={ref}
      {...props}
    />
  );
});

Link.displayName = "Link";

/**
 * Use this outlet component when you do not need to pass any context to your children.
 * @returns An outlet component that does not pass any context to its children
 */
const NoContextOutlet = () => {
  return <Outlet />;
};

const createTypedOutlet = <T,>(name: string) => {
  const contextTypeSymbol = Symbol();

  // Backs the non-routed `ContextProvider`. Routed children read context off
  // React Router's outlet context; inline children read it here. Consumers
  // prefer this when set, so both paths resolve through the same hooks.
  const DirectContext = createContext<T | undefined>(undefined);

  // Memoize the wrapped context so OutletContext consumers don't see a fresh
  // reference every render. Without this, every render of the OutletProvider
  // (driven by parent re-renders or query refetches) propagates a new
  // OutletContext value to every consumer in the subtree.
  const OutletProvider = ({ context }: { context: T }) => {
    const wrapped = useMemo(
      () => ({ ...context, __contextOutletType: contextTypeSymbol }),
      [context],
    );
    return <Outlet context={wrapped} />;
  };

  // Provides the context to direct children rather than a routed `<Outlet>`,
  // for mounting an outlet consumer outside of routing.
  const ContextProvider = ({
    context,
    children,
  }: {
    context: T;
    children: ReactNode;
  }) => (
    <DirectContext.Provider value={context}>{children}</DirectContext.Provider>
  );

  const useContext = (): T => {
    const direct = useReactContext(DirectContext);
    const outlet = useOutletContext<{ __contextOutletType: symbol } & T>();
    if (direct !== undefined) {
      return direct;
    }
    if (outlet?.__contextOutletType !== contextTypeSymbol) {
      throw new Error(
        `use${name}OutletContext must be used within <${name}Outlet>. ` +
          `If this component intentionally renders outside the provider, use use${name}OutletContextIfAvailable instead.`,
      );
    }
    return outlet;
  };

  const useOptionalContext = (): T | undefined => {
    const direct = useReactContext(DirectContext);
    const outlet = useOutletContext<{ __contextOutletType: symbol } & T>();
    if (direct !== undefined) {
      return direct;
    }
    if (outlet?.__contextOutletType !== contextTypeSymbol) {
      return undefined;
    }
    return outlet;
  };

  return {
    OutletProvider,
    ContextProvider,
    useContext,
    useOptionalContext,
  } as const;
};

export {
  Route,
  Routes,
  useBeforeUnload,
  useBlocker,
  useSearchParams,
  useParams,
  useLocation,
  matchRoutes,
  NoContextOutlet,
  createSearchParams,
  MemoryRouter,
  generatePath,
  useNavigation,
  useLoaderData,
  useRevalidator,
  createTypedOutlet,

  // Modified for workspace slug
  Link,
  LinkButton,
  RouterLink,
  useNavigate,
  Navigate,
  useMatch,
  useMatches,
  useOriginWithWorkspace,
};

export type {
  To,
  NavigateOptions,
  NavigateFunction,
  RRRouteObject,
  LoaderFunction,
};

// Re-export space-related types for route definitions
export {
  type HTRouteObject as RouteObject,
  type SpacesAccess,
} from "src/components/spaces/types";

// Re-export test utilities
export { createMemoryRouter, RouterProvider } from "react-router-dom";
